import type { ReactNode } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { ListCard } from '@/components/list-card'
import { CardTitle } from '@/components/ui/card'
import { UpvoteButton } from './upvote-button'
import type { FeedbackItem } from '@/hooks/use-feedback'
import {
  feedbackKindBadgeVariant,
  feedbackStatusBadgeVariant,
  formatFeedbackElicitation,
  formatFeedbackKind,
  formatFeedbackStatus,
} from '@/lib/feedback-display'

export type FeedbackListCardProps = {
  item: FeedbackItem
  onToggleVote?: (item: FeedbackItem) => void
  isVoting?: boolean
  /** Extra footer content (e.g. admin status controls). */
  footerExtra?: ReactNode
  className?: string
}

/**
 * Board card for a feedback item. Anonymous by design — reporter identity is
 * never rendered here (admin surfaces show it separately).
 */
export function FeedbackListCard({
  item,
  onToggleVote,
  isVoting,
  footerExtra,
  className,
}: FeedbackListCardProps) {
  const elicitationLabel = formatFeedbackElicitation(item.elicitation)

  return (
    <ListCard
      className={className}
      agentHighlight={`feedback:${item.id}`}
      header={
        <div className="flex min-w-0 flex-col gap-2">
          <div className="pointer-events-none flex flex-wrap items-center gap-1.5">
            <Badge variant={feedbackKindBadgeVariant(item.kind)}>
              {formatFeedbackKind(item.kind)}
            </Badge>
            <Badge variant={feedbackStatusBadgeVariant(item.status)}>
              {formatFeedbackStatus(item.status)}
            </Badge>
            {elicitationLabel ? <Badge variant="outline">{elicitationLabel}</Badge> : null}
          </div>
          <CardTitle className="pointer-events-none min-w-0 break-words text-base">
            {item.title}
          </CardTitle>
        </div>
      }
      body={
        item.body ? (
          <p className="line-clamp-3 text-sm text-muted-foreground">{item.body}</p>
        ) : null
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="pointer-events-none text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
          </span>
          <div className="flex items-center gap-1.5">
            {footerExtra}
            {onToggleVote ? (
              <UpvoteButton
                count={item.vote_count}
                voted={item.my_vote === true}
                disabled={isVoting}
                onToggle={() => onToggleVote(item)}
              />
            ) : (
              <span className="pointer-events-none text-xs font-semibold tabular-nums text-muted-foreground">
                {item.vote_count} {item.vote_count === 1 ? 'vote' : 'votes'}
              </span>
            )}
          </div>
        </div>
      }
    />
  )
}
